/**
 * One list for a module entry's Documents tab, so staff can track everything in one place:
 * - "uploaded": files staff attached to the entry themselves,
 * - "client":   files the client sent through a request, now filed into this entry,
 * - "requested": documents asked of the client for this entry that have not come in yet.
 * Pure, so it is cheap to test; the panel only renders what this returns.
 */

import { P } from "const/permissions";

const STALE_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Requests whose documents file into this entry. */
export const requestsForEntry = (requests = [], moduleType, entryId) =>
  requests.filter(
    (request) =>
      request?.targetModuleType === moduleType &&
      entryId != null &&
      String(request?.targetModuleEntryId) === String(entryId)
  );

/** What a requested document is waiting on, from the request's and the item's state. */
export const requestedItemStatus = (request, item) => {
  if (request?.status === "REVOKED") return { label: "Cancelled", tone: "neutral", rank: 4 };
  if (request?.status === "SUBMITTED") {
    return item?.state === "FULFILLED"
      ? { label: "Received", tone: "success", rank: 3 }
      : { label: "Not provided", tone: "neutral", rank: 4 };
  }
  if (item?.state === "FLAGGED") return { label: "Needs re-upload", tone: "danger", rank: 0 };
  if (item?.state === "FULFILLED") {
    return { label: "Uploaded, not submitted", tone: "info", rank: 1 };
  }
  return { label: "Waiting on client", tone: "warning", rank: 0 };
};

const isStale = (request, now) =>
  request?.status === "ACTIVE" &&
  request?.createdAt &&
  now - new Date(request.createdAt).getTime() > STALE_DAYS * DAY_MS;

/**
 * Rows for the table. A submitted request's received files are already filed into the entry
 * and listed as "client" rows, so they are not repeated as requested rows.
 */
export const buildEntryDocumentRows = (
  { uploads = [], linked = [], requests = [] },
  now = Date.now()
) => {
  const uploadRows = uploads.map((doc) => ({
    key: `uploaded-${doc.id || doc.url}`,
    kind: "uploaded",
    name: doc.name || "Document",
    detail: doc.url,
    status: doc.signedAt
      ? { label: "Signed", tone: "success", rank: 3 }
      : { label: "Uploaded", tone: "neutral", rank: 3 },
    date: doc.addedAt || doc.uploadedAt || null,
    by: doc.addedBy || null,
    source: doc,
  }));

  const linkedRows = linked.map((doc) => ({
    key: `client-${doc.linkId}`,
    kind: "client",
    name: doc.itemLabel || doc.label || doc.originalFilename || "Document",
    detail: doc.originalFilename,
    status: doc.replacedSinceLinked
      ? { label: "Updated by client", tone: "info", rank: 2 }
      : { label: "Received", tone: "success", rank: 3 },
    date: doc.linkedAt || doc.uploadedAt || doc.createdAt || null,
    by: "Client",
    source: doc,
  }));

  const requestedRows = requests.flatMap((request) =>
    (request.items || [])
      .filter((item) => !(request.status === "SUBMITTED" && item.state === "FULFILLED"))
      .filter((item) => !(request.status === "SUBMITTED" && !item.required))
      .map((item) => ({
        key: `requested-${request.id}-${item.id}`,
        kind: "requested",
        name: item.label || "Document",
        detail: request.title,
        status: requestedItemStatus(request, item),
        date: request.createdAt || null,
        by: null,
        stale: isStale(request, now),
        optional: !item.required,
        source: { request, item },
      }))
  );

  // What needs attention first, then newest first.
  return [...requestedRows, ...linkedRows, ...uploadRows].sort(
    (a, b) =>
      a.status.rank - b.status.rank || String(b.date || "").localeCompare(String(a.date || ""))
  );
};

export const countRows = (rows) => ({
  all: rows.length,
  uploaded: rows.filter((row) => row.kind === "uploaded").length,
  client: rows.filter((row) => row.kind === "client").length,
  requested: rows.filter((row) => row.kind === "requested").length,
  waiting: rows.filter((row) => row.kind === "requested" && row.status.rank <= 1).length,
});

/** Each module's own update key: the one its record Save needs, so the one that edits its files. */
const ENTRY_UPDATE_PERMISSION = Object.freeze({
  VISA: P.VISA.UPDATE,
  INSURANCE: P.INSURANCE.UPDATE,
  SKILL_ASSESSMENT: P.SKILL.UPDATE,
  RPL_CLIENT: P.RPL_CLIENT.UPDATE,
  ADMISSION: P.ADMISSIONS.UPDATE,
});

/**
 * What the current user may do to an entry's own files, mirroring the server: adding takes the
 * module's update key, removing also takes the document delete key. Files picked for a record not
 * created yet are only form state, so nothing gates them. `can` is the permission check.
 */
export const entryDocumentRights = ({ moduleType, persisted, readOnly = false, can }) => {
  if (readOnly) return { canAdd: false, canRemove: false };
  if (!persisted) return { canAdd: true, canRemove: true };
  const updateKey = ENTRY_UPDATE_PERMISSION[moduleType];
  const canEdit = Boolean(updateKey) && can(updateKey);
  return { canAdd: canEdit, canRemove: canEdit && can(P.CLIENT_DOCUMENT.DELETE) };
};
